import type { TranslationKey } from "@/localisation/types";
import { useTenant } from "@/providers/session";
import { useGetCompanyInfo } from "@/queries/companies-info/fetch";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { MonthDayPicker } from "@/components/shared/form/month-day-picker";
import { TextAreaInput } from "@/components/shared/form/text-area-input";
import { TextInput } from "@/components/shared/form/text-input";
import { AddressList } from "@/components/ui/address-list";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useUpdateCompanyInfo } from "@/queries/companies-info/update";
import { useUpdateCompanyAddresses } from "@/queries/companies/addresses/update";
import {
  type CompanyAddress,
  LegalForm,
  useGetCompany,
} from "@/queries/companies/fetch";
import { useUpdateCompany } from "@/queries/companies/update";
import { useCreateFiscalYear } from "@/queries/fiscal-years/create";
import { useDeleteFiscalYear } from "@/queries/fiscal-years/delete";
import { useGetFiscalYears } from "@/queries/fiscal-years/fetch";
import { useUpdateFiscalYear } from "@/queries/fiscal-years/update";
import { SquarePen } from "lucide-react";
import { type CompanyFormData, companyFormSchema } from "./validations/company";

interface FiscalYearFormValues {
  startDate: Date | undefined;
}

interface OffsetFiscalYearFormValues {
  startDate: Date | null;
}

export default function BasicCompanyData() {
  const { t } = useTranslation();
  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false);
  const [isFiscalYearDialogOpen, setIsFiscalYearDialogOpen] = useState(false);
  const [isOffsetFiscalYearDialogOpen, setIsOffsetFiscalYearDialogOpen] =
    useState(false);

  const tenant = useTenant();
  const { companyName, tenantId, isVorsteuerabzug } = tenant;
  const { companyInfo, refetch: refetchCompanyInfo } = useGetCompanyInfo();

  const { companyData, companyLoading, companyError, refetchCompany } =
    useGetCompany();

  const vatId = companyData?.company.vatId;
  const thirteenBType = companyData?.company.thirteenBType;
  const legalForm = companyData?.company.legalForm;

  const { updateCompanyInfo } = useUpdateCompanyInfo();
  const { updateCompany } = useUpdateCompany();
  const { updateCompanyAddresses } = useUpdateCompanyAddresses();

  const form = useForm<CompanyFormData>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      companyName: companyName || "",
      vatId: vatId || "",
      companyDescription: companyInfo?.companyDescription || "",
      inputTaxDeduction: isVorsteuerabzug,
      thirteenBType: thirteenBType || "STANDARD",
      addresses: [],
      legalForm: legalForm || LegalForm.OTHER,
    },
    mode: "onBlur",
    resetOptions: {
      keepDirtyValues: false,
      keepErrors: false,
    },
  });

  useEffect(() => {
    const newValues = {
      companyName: companyName || "",
      vatId: vatId || "",
      companyDescription: companyInfo?.companyDescription || "",
      inputTaxDeduction: isVorsteuerabzug,
      thirteenBType: thirteenBType || "STANDARD",
      legalForm: legalForm || LegalForm.OTHER,
      addresses:
        companyData?.company?.addresses?.map((addr: CompanyAddress) => ({
          addressLine1: addr.addressLine1 || "",
          postcode: addr.postcode || "",
          city: addr.city || "",
          addressLine2: addr.addressLine2 || "",
          country: addr.country || "DE",
        })) || [],
    };
    form.reset(newValues);
  }, [
    companyName,
    vatId,
    companyInfo?.companyDescription,
    isVorsteuerabzug,
    thirteenBType,
    legalForm,
    companyData?.company?.addresses,
    form.reset,
  ]);

  useEffect(() => {
    refetchCompany();
    refetchCompanyInfo();
  }, [companyName, refetchCompany, refetchCompanyInfo]);

  const handleCompanyNameSave = async (data: CompanyFormData) => {
    try {
      await updateCompany({
        variables: {
          companyId: tenantId,
          updatedFields: {
            companyName: data.companyName,
          },
        },
      });
      toast.success(t("basic_data.save_success"));
    } catch (error) {
      // Revert the state if the mutation fails
      form.setValue("companyName", companyName || "");
      console.error("Failed to update company name:", error);
      toast.error(t("basic_data.save_error"));
    }
  };

  const handleLegalFormSave = async (data: CompanyFormData) => {
    try {
      await updateCompany({
        variables: {
          companyId: tenantId,
          updatedFields: {
            legalForm: data.legalForm,
          },
        },
      });
      toast.success(t("basic_data.save_success"));
    } catch (error) {
      // Revert the state if the mutation fails
      form.setValue("legalForm", legalForm || LegalForm.OTHER);
      console.error("Failed to update legal form:", error);
      toast.error(t("basic_data.save_error"));
    }
  };

  const handleVatIdSave = async (data: CompanyFormData) => {
    try {
      await updateCompany({
        variables: {
          companyId: tenantId,
          updatedFields: {
            vatId: data.vatId,
          },
        },
      });
      toast.success(t("basic_data.save_success"));
    } catch (error) {
      // Revert the state if the mutation fails
      form.setValue("vatId", vatId || "");
      console.error("Failed to update VAT ID:", error);
      toast.error(t("basic_data.save_error"));
    }
  };

  const handleCompanyDescriptionSave = async (data: CompanyFormData) => {
    try {
      const res = await updateCompanyInfo({
        variables: {
          companyInfoId: companyInfo?.companyInfoId,
          updatedFields: {
            companyDescription: data.companyDescription,
          },
        },
      });
      const newDescription = res.data?.updateCompanyInfo.companyDescription;
      form.setValue("companyDescription", newDescription);
      toast.success(t("basic_data.save_success"));
    } catch (error) {
      console.error("Failed to update company description:", error);
      toast.error(t("basic_data.save_error"));
    }
  };

  const handleInputTaxDeductionChange = async (checked: boolean) => {
    form.setValue("inputTaxDeduction", checked);

    try {
      await updateCompany({
        variables: {
          companyId: tenantId,
          updatedFields: {
            vorsteuerabzug: checked,
          },
        },
      });
      toast.success(t("basic_data.save_success"));
    } catch (error) {
      // Revert the state if the mutation fails
      form.setValue("inputTaxDeduction", !checked);
      console.error("Failed to update input tax deduction:", error);
      toast.error(t("basic_data.save_error"));
    }
  };

  const handleCompanyTypeChange = async (data: CompanyFormData) => {
    try {
      await updateCompany({
        variables: {
          companyId: tenantId,
          updatedFields: {
            thirteenBType: data.thirteenBType,
          },
        },
      });
      toast.success(t("basic_data.save_success"));
    } catch (error) {
      // Revert the state if the mutation fails
      form.setValue("thirteenBType", thirteenBType || "STANDARD");
      console.error("Failed to update company type:", error);
      toast.error(t("basic_data.save_error"));
    }
  };

  const handleAddressesUpdate = async (data: CompanyFormData) => {
    try {
      const formattedAddresses = data.addresses.map((addr) => ({
        addressLine1: addr.addressLine1,
        addressLine2: addr.addressLine2,
        city: addr.city,
        postcode: addr.postcode,
        country: addr.country,
      }));

      await updateCompanyAddresses({
        variables: {
          companyId: tenantId,
          addresses: formattedAddresses,
        },
      });

      await refetchCompany();
      setIsAddressDialogOpen(false);
    } catch (error) {
      console.error("Failed to update addresses:", error);
    }
  };

  const { createFiscalYear } = useCreateFiscalYear();
  const { updateFiscalYear } = useUpdateFiscalYear();
  const { deleteFiscalYear } = useDeleteFiscalYear();

  const { fiscalYearData, refetchFiscalYear } = useGetFiscalYears();

  // Fiscal year form
  const fiscalYearForm = useForm<FiscalYearFormValues>({
    defaultValues: {
      startDate: undefined,
    },
    mode: "onBlur",
  });

  const { isDirty: isFiscalYearDirty, isValid: isFiscalYearValid } =
    fiscalYearForm.formState;
  const [isSavingFiscalYear, setIsSavingFiscalYear] = useState(false);

  // Offset fiscal year form
  const offsetFiscalYearForm = useForm<OffsetFiscalYearFormValues>({
    defaultValues: {
      startDate: null,
    },
    mode: "onBlur",
  });

  const [isSavingOffsetFiscalYear, setIsSavingOffsetFiscalYear] =
    useState(false);

  // Initialize offset fiscal year form when tenant data changes
  useEffect(() => {
    const fiscalYearStartDay = companyData?.company?.fiscalYearStartDay;
    const fiscalYearStartMonth = companyData?.company?.fiscalYearStartMonth;

    if (
      fiscalYearStartDay &&
      fiscalYearStartMonth &&
      !(fiscalYearStartDay === 1 && fiscalYearStartMonth === 1)
    ) {
      const date = new Date(2024, fiscalYearStartMonth - 1, fiscalYearStartDay);
      offsetFiscalYearForm.reset({
        startDate: date,
      });
    } else {
      offsetFiscalYearForm.reset({ startDate: null });
    }
  }, [companyData]);

  useEffect(() => {
    const firstYear = fiscalYearData?.fiscalYears?.[0];
    if (firstYear) {
      fiscalYearForm.reset({
        startDate: new Date(firstYear.startDate),
      });
    } else {
      fiscalYearForm.reset({ startDate: undefined });
    }
  }, [fiscalYearData, fiscalYearForm]);

  const onFiscalYearSubmit = async (values: FiscalYearFormValues) => {
    try {
      setIsSavingFiscalYear(true);
      const existing = fiscalYearData?.fiscalYears?.[0];

      if (!values.startDate && existing) {
        // Delete fiscal year if start date is cleared
        await deleteFiscalYear({
          variables: {
            fiscalYearId: existing.fiscalYearId,
          },
        });
        toast.success(t("fiscal_year.delete_success"));
      } else if (values.startDate) {
        // Create or update fiscal year
        const date = new Date(
          values.startDate.getFullYear(),
          values.startDate.getMonth(),
          values.startDate.getDate(),
        );
        if (existing) {
          await updateFiscalYear({
            variables: {
              fiscalYearId: existing.fiscalYearId,
              updatedFields: {
                startDate: date,
                year: date.getFullYear(),
              },
            },
          });
        } else {
          await createFiscalYear({
            variables: {
              fiscalYear: {
                startDate: date,
                year: date.getFullYear(),
              },
            },
          });
        }
        toast.success(t("fiscal_year.save_success"));
      }

      await refetchFiscalYear();
      setIsFiscalYearDialogOpen(false);
    } catch (error) {
      toast.error(t("fiscal_year.save_error"));
    } finally {
      setIsSavingFiscalYear(false);
    }
  };

  const onOffsetFiscalYearSubmit = async (
    values: OffsetFiscalYearFormValues,
  ) => {
    try {
      setIsSavingOffsetFiscalYear(true);

      const fiscalYearStartDay = values.startDate?.getDate() || 1;
      const fiscalYearStartMonth = values.startDate
        ? values.startDate.getMonth() + 1
        : 1;

      await updateCompany({
        variables: {
          companyId: tenantId,
          updatedFields: {
            fiscalYearStartDay,
            fiscalYearStartMonth,
          },
        },
      });
      toast.success(t("fiscal_year.save_success"));
      await refetchCompany();
      setIsOffsetFiscalYearDialogOpen(false);
    } catch (error) {
      toast.error(t("fiscal_year.save_error"));
    } finally {
      setIsSavingOffsetFiscalYear(false);
    }
  };

  // Update the UI section to use companyData
  const fiscalYearStartDay = companyData?.company?.fiscalYearStartDay;
  const fiscalYearStartMonth = companyData?.company?.fiscalYearStartMonth;

  return (
    <Card>
      <CardContent>
        <FormProvider {...form}>
          <div className="space-y-0">
            {/* Company Name */}
            <div className="flex items-center py-4">
              <FormLabel className="w-56 flex-shrink-0">
                {t("basic_data.name")}
              </FormLabel>
              <div className="flex-1 ml-12 flex flex-col">
                <TextInput
                  name="companyName"
                  onBlur={(e) => {
                    if (form.formState.dirtyFields.companyName) {
                      form.handleSubmit(handleCompanyNameSave)(e);
                    }
                  }}
                />
              </div>
            </div>
            <Separator />

            {/* VAT ID */}
            <div className="flex items-center py-4">
              <FormLabel className="w-56 flex-shrink-0">
                {t("basic_data.vat_id")}
              </FormLabel>
              <div className="flex-1 ml-12 flex flex-col">
                <TextInput
                  name="vatId"
                  onBlur={(e) => {
                    if (form.formState.dirtyFields.vatId) {
                      form.handleSubmit(handleVatIdSave)(e);
                    }
                  }}
                />
              </div>
            </div>
            <Separator />

            {/* Address */}
            <div className="flex items-center py-4">
              <Label className="w-56 flex-shrink-0">
                {t("basic_data.address")}
              </Label>
              <div className="flex-1 ml-12 flex items-center justify-between">
                <div>
                  {companyLoading ? (
                    <div className="text-sm text-gray-500">
                      {t("generic_loading")}
                    </div>
                  ) : companyError ? (
                    <div className="text-sm text-red-500">
                      {t("basic_data.error_loading_addresses")}
                    </div>
                  ) : (
                    companyData?.company?.addresses?.map(
                      (addr: CompanyAddress, index: number) => (
                        <div key={index} className="text-sm text-gray-900">
                          <div>{addr.addressLine1}</div>
                          {addr.addressLine2 && <div>{addr.addressLine2}</div>}
                          <div>{`${addr.postcode} ${addr.city}`}</div>
                          <div>{addr.country}</div>
                        </div>
                      ),
                    )
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-4"
                  onClick={() => setIsAddressDialogOpen(true)}
                >
                  <SquarePen className="h-4 w-4 mr-2" />
                  {t("basic_data.edit")}
                </Button>
              </div>
            </div>
            <Separator />

            {/* Input Tax Deduction */}
            <FormField
              control={form.control}
              name="inputTaxDeduction"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center py-4">
                    <div className="w-56 flex-shrink-0 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {t("basic_data.input_tax_deduction")}
                    </div>
                    <div className="flex-1 ml-12 flex items-center justify-end">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={handleInputTaxDeductionChange}
                          className="data-[state=checked]:bg-green-500"
                        />
                      </FormControl>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Separator />

            {/* Company Description */}
            <div className="flex items-start py-4">
              <div className="w-56 flex-shrink-0">
                <FormLabel htmlFor="company-description">
                  {t("company_description")}
                </FormLabel>
                <p className="text-xs text-gray-600 mt-1">
                  {t("basic_data.company_description_help")}
                </p>
              </div>
              <div className="flex-1 ml-12 flex flex-col">
                <TextAreaInput
                  name="companyDescription"
                  placeholder={t("basic_data.company_description_placeholder")}
                  className="min-h-[200px] resize-none w-full"
                  onBlur={(e) => {
                    if (form.formState.dirtyFields.companyDescription) {
                      form.handleSubmit(handleCompanyDescriptionSave)(e);
                    }
                  }}
                />
              </div>
            </div>
            <Separator />

            {/* Legal Form */}
            <div className="flex items-center py-4">
              <FormLabel className="w-56 flex-shrink-0">
                {t("basic_data.legal_form")}
              </FormLabel>
              <div className="flex-1 ml-12 flex flex-col">
                <FormField
                  control={form.control}
                  name="legalForm"
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.handleSubmit(handleLegalFormSave)();
                        }}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t(
                                "basic_data.legal_form_placeholder",
                              )}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.values(LegalForm).map((value) => (
                            <SelectItem key={value} value={value}>
                              {
                                t(
                                  `basic_data.legal_forms_dropdown.${value.toLowerCase()}` as TranslationKey,
                                ) as string
                              }
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <Separator />

            {/* Stub Fiscal Year */}
            <div className="flex items-center py-4">
              <div className="w-56 flex-shrink-0">
                <Label>{t("basic_data.stub_fiscal_year")}</Label>
                <p className="text-xs text-gray-600 mt-1">
                  {t("basic_data.stub_fiscal_year_help")}
                </p>
              </div>
              <div className="flex-1 ml-12 flex items-center justify-between">
                <div className="text-sm text-gray-900">
                  {fiscalYearData?.fiscalYears?.[0] ? (
                    <>
                      {t("basic_data.start_at")}{" "}
                      {new Date(
                        fiscalYearData.fiscalYears[0].startDate,
                      ).toLocaleDateString("de-DE")}
                    </>
                  ) : (
                    <span className="text-muted-foreground">
                      {t("basic_data.not_set")}
                    </span>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-4"
                  onClick={() => setIsFiscalYearDialogOpen(true)}
                >
                  <SquarePen className="h-4 w-4 mr-2" />
                  {t("basic_data.edit")}
                </Button>
              </div>
            </div>
            <Separator />

            {/* Offset Fiscal Year */}
            <div className="flex items-center py-4">
              <div className="w-56 flex-shrink-0">
                <Label>{t("basic_data.offset_fiscal_year")}</Label>
                <p className="text-xs text-gray-600 mt-1">
                  {t("basic_data.offset_fiscal_year_help")}
                </p>
              </div>
              <div className="flex-1 ml-12 flex items-center justify-between">
                <div className="text-sm text-gray-900">
                  {companyLoading ? (
                    <span className="text-muted-foreground">Lädt...</span>
                  ) : fiscalYearStartDay &&
                    fiscalYearStartMonth &&
                    !(
                      fiscalYearStartDay === 1 && fiscalYearStartMonth === 1
                    ) ? (
                    <>
                      {(() => {
                        // Calculate start date
                        const startDate = new Date(
                          2024,
                          fiscalYearStartMonth - 1,
                          fiscalYearStartDay,
                        );
                        // Calculate end date (day before next year's start)
                        const endDate = new Date(
                          2024,
                          fiscalYearStartMonth - 1,
                          fiscalYearStartDay,
                        );
                        endDate.setFullYear(endDate.getFullYear() + 1);
                        endDate.setDate(endDate.getDate() - 1);

                        return `${startDate.toLocaleDateString("de-DE", {
                          day: "2-digit",
                          month: "2-digit",
                        })} - ${endDate.toLocaleDateString("de-DE", {
                          day: "2-digit",
                          month: "2-digit",
                        })}`;
                      })()}
                    </>
                  ) : (
                    <span className="text-muted-foreground">
                      {t("basic_data.not_set")}
                    </span>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-4"
                  onClick={() => setIsOffsetFiscalYearDialogOpen(true)}
                >
                  <SquarePen className="h-4 w-4 mr-2" />
                  {t("basic_data.edit")}
                </Button>
              </div>
            </div>
            <Separator />
          </div>
        </FormProvider>
      </CardContent>

      <Dialog open={isAddressDialogOpen} onOpenChange={setIsAddressDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col p-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle>{t("basic_data.edit_addresses")}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6">
            <Form {...form}>
              <AddressList
                form={form}
                name="addresses"
                description={t("basic_data.addresses_description")}
              />
              <div className="sticky bottom-0 flex justify-end py-4 mt-6 bg-white border-t">
                <Button
                  type="button"
                  onClick={form.handleSubmit(handleAddressesUpdate)}
                >
                  {t("basic_data.save")}
                </Button>
              </div>
            </Form>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isFiscalYearDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            // Reset form to initial values when closing without saving
            const firstYear = fiscalYearData?.fiscalYears?.[0];
            if (firstYear) {
              fiscalYearForm.reset({
                startDate: new Date(firstYear.startDate),
              });
            } else {
              fiscalYearForm.reset({ startDate: undefined });
            }
          }
          setIsFiscalYearDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("fiscal_year.title")}</DialogTitle>
          </DialogHeader>
          <FormProvider {...fiscalYearForm}>
            <div className="flex flex-col gap-6">
              <div className="flex items-start gap-8">
                <div className="w-64">
                  <div className="space-y-1">
                    <Label htmlFor="startDate">
                      {t("fiscal_year.start_date")}
                    </Label>
                    <Controller<FiscalYearFormValues>
                      name="startDate"
                      control={fiscalYearForm.control}
                      render={({ field }) => (
                        <DateTimePicker
                          {...field}
                          value={field.value || undefined}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsFiscalYearDialogOpen(false)}
                  className="mr-2"
                >
                  {t("fiscal_year.cancel")}
                </Button>
                <Button
                  onClick={fiscalYearForm.handleSubmit((values) => {
                    onFiscalYearSubmit(values);
                  })}
                  disabled={isSavingFiscalYear}
                >
                  {t("fiscal_year.save")}
                </Button>
              </div>
            </div>
          </FormProvider>
        </DialogContent>
      </Dialog>

      {/* Offset Fiscal Year Dialog */}
      <Dialog
        open={isOffsetFiscalYearDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            // Reset form to initial values when closing without saving
            if (fiscalYearStartDay && fiscalYearStartMonth) {
              const date = new Date(
                2024,
                fiscalYearStartMonth - 1,
                fiscalYearStartDay,
              );
              offsetFiscalYearForm.reset({
                startDate: date,
              });
            } else {
              offsetFiscalYearForm.reset({ startDate: null });
            }
          }
          setIsOffsetFiscalYearDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("basic_data.offset_fiscal_year")}</DialogTitle>
          </DialogHeader>
          <FormProvider {...offsetFiscalYearForm}>
            <div className="flex flex-col gap-6">
              <div className="flex items-start gap-8">
                <div className="w-64">
                  <div className="space-y-1">
                    <Label htmlFor="startDate">
                      {t("fiscal_year.start_date")}
                    </Label>
                    <Controller<OffsetFiscalYearFormValues>
                      name="startDate"
                      control={offsetFiscalYearForm.control}
                      render={({ field }) => <MonthDayPicker {...field} />}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsOffsetFiscalYearDialogOpen(false)}
                  className="mr-2"
                >
                  {t("fiscal_year.cancel")}
                </Button>
                <Button
                  onClick={offsetFiscalYearForm.handleSubmit((values) => {
                    onOffsetFiscalYearSubmit(values);
                  })}
                  disabled={isSavingOffsetFiscalYear}
                >
                  {t("fiscal_year.save")}
                </Button>
              </div>
            </div>
          </FormProvider>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
